/** AI Reply Coach — merchant replies from the agent's synced Gmail, tagged and answered with click-to-use drafts. */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import api from '../lib/api';
import { PageHeader } from '../components/finance/financeUi';
import { GmailSyncButton } from '../components/comm/GmailSync';
import { CoachPanel } from '../components/coach/CoachPanel';
import { Tags } from '../components/coach/CoachTags';

function ReplyList({ rows, active, onPick }) {
  if (!rows.length) return <div className="p-6 text-center text-sm text-muted-foreground" data-testid="coach-empty"><Inbox className="mx-auto mb-2 h-6 w-6" />No merchant replies yet. Connect Gmail in Settings; replies from your leads and merchants show up here.</div>;
  return rows.map((r) => (
    <button key={r.gmail_id} type="button" onClick={() => onPick(r.gmail_id)} data-testid={`coach-item-${r.gmail_id}`}
      className={`block w-full border-b p-4 text-left transition-colors hover:bg-muted/60 ${active === r.gmail_id ? 'bg-muted' : ''}`}>
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground"><span className="truncate">{r.business || r.from}</span><span className="shrink-0">{r.sent_at ? new Date(r.sent_at).toLocaleDateString() : ''}</span></div>
      <p className="mt-1 truncate text-sm font-medium">{r.subject || '(no subject)'}</p>
      <p className="line-clamp-2 text-xs text-muted-foreground">{r.snippet}</p>
      <div className="mt-2 flex items-center gap-2">{r.analysis ? <Tags a={r.analysis} compact /> : <span className="text-[11px] text-primary">Not coached yet</span>}{r.replied_at && <span className="text-[11px] text-emerald-700">Replied</span>}</div>
    </button>));
}

export default function ReplyCoachPage() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const active = params.get('m');
  const load = useCallback(() => api.request('/reply-coach/inbox').then((d) => setRows(d.messages)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-6" data-testid="reply-coach-page">
      <PageHeader eyebrow="CRM" title="Reply Coach" subtitle="Every merchant reply, read and tagged by AI with a reply ready to paste. Hormozi style by default, NEPQ when there's an objection or pushback. Nothing is sent until you send it." />
      <GmailSyncButton />
      <div className="grid overflow-hidden rounded-xl border bg-card lg:grid-cols-[360px_1fr]">
        <div className="max-h-[75vh] overflow-y-auto border-b lg:border-b-0 lg:border-r" data-testid="coach-list">{rows ? <ReplyList rows={rows} active={active} onPick={(id) => setParams({ m: id })} /> : <p className="p-6 text-sm text-muted-foreground">Loading…</p>}</div>
        <div className="p-6">{active ? <CoachPanel gmailId={active} onChanged={load} /> : <p className="py-20 text-center text-sm text-muted-foreground" data-testid="coach-pick">Pick a reply to coach.</p>}</div>
      </div>
    </div>
  );
}
